import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriminalWizardContext } from '../../../hooks/useCaseRequestWizard';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import { calcAgeFromDob, exceedsMax, maxCharLimitExceededError, todayIsoDateLocal } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { IconWarningTriangle } from '../../../components/icons/WizardIcons';
import { SectionCard } from '../WizardComponents';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import { CriminalWizardActions } from './CriminalWizardActions';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { criminalWizardPath, CriminalWizardShell, FieldError, inpClass } from './CriminalWizardShell';
import { useCriminalLookups } from './useCriminalLookups';

const MAX_NAME = 255;

export default function CriminalStep2VictimDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCriminalWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const maxLen = useWizardMaxLengthChange(setErrors);
  const { genders, loading: gendersLoading } = useCriminalLookups('step2');

  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const age = useMemo(() => calcAgeFromDob(draft.victimDob), [draft.victimDob]);
  const under16 = typeof age === 'number' && age < 16;

  const validate = (): boolean => {
    const extra: RequiredErrors = {};
    if (exceedsMax(draft.victimName, MAX_NAME)) extra.victimName = maxCharLimitExceededError();
    setErrors(extra);
    if (Object.keys(extra).length === 0) return true;
    const specs: RequiredFieldSpec[] = [
      { key: 'victimName', label: f.victimName, value: draft.victimName, elementId: 'crim-victimName' },
    ];
    focusFirstInvalid(specs, extra);
    return false;
  };

  const next = () => {
    if (!validate()) return;
    nav(criminalWizardPath(3, draft.draftRequestId));
  };

  return (
    <CriminalWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionVictimDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.victimName}</label>
            <input
              id="crim-victimName"
              className={inpClass(Boolean(errors.victimName))}
              type="text"
              value={draft.victimName}
              onChange={(e) => setDraft((d) => ({ ...d, victimName: maxLen('victimName', e.target.value, MAX_NAME) }))}
              placeholder={f.enterVictimName}
            />
            <FieldError message={errors.victimName} />
          </div>

          <div className="ccr-fg">
            <label>{f.dateOfBirth}</label>
            <FormattedDateInput
              value={draft.victimDob}
              maxDate={maxDob}
              enableYmdViews
              onChange={(next) => setDraft((d) => ({ ...d, victimDob: next }))}
            />
            {under16 ? (
              <div className="under16">
                <IconWarningTriangle />
                {f.childUnder16}
              </div>
            ) : null}
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>{f.age}</label>
            <input className="ccr-inp" type="text" value={typeof age === 'number' ? String(age) : ''} readOnly disabled placeholder="" />
          </div>
          <div className="ccr-fg">
            <label>{f.gender}</label>
            <WizardSelect
              value={draft.victimGenderId}
              disabled={gendersLoading}
              placeholder={f.selectGender}
              options={genders.map((g) => ({ id: g.id, label: g.name }))}
              onChange={(id) => {
                const name = genders.find((g) => g.id === id)?.name ?? '';
                setDraft((d) => ({ ...d, victimGenderId: id, victimGender: name }));
              }}
            />
          </div>
        </div>
      </SectionCard>

      <CriminalWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(criminalWizardPath(1, draft.draftRequestId))}
        onNext={next}
        validateBeforeSave={validate}
      />
    </CriminalWizardShell>
  );
}

